import { NavigateFunction } from 'react-router-dom';
import { DEFAULT_LANGUAGE, LANGUAGE_LIST } from 'src/contstant/commonLabels';
import routePaths from 'src/contstant/routePaths';
import { Task, taskContent } from 'src/types/helios/Task';

/**
 * Helper functions for handling task-related operations including navigation
 */

/**
 * Navigates to the task details page with proper URL parameters
 * @param navigate - React Router navigation function
 * @param data - Task object containing navigation parameters
 * @param isReplace - Whether to replace current history entry
 * @param openNewTab - Whether to open in a new browser tab
 */
export function openTaskDetailsPage(
  navigate: NavigateFunction,
  data: Task,
  isReplace?: boolean,
  openNewTab?: boolean,
) {
  if (data) {
    const url = getTaskDetailsUrl(data);
    // Handle different navigation modes
    if (openNewTab === true) {
      window.open(url, '_blank');
    } else {
      if (isReplace === true) {
        navigate(url, { replace: true });
      } else {
        navigate(url);
      }
    }
  }
}

export function getTaskDetailsUrl(data: Task) {
  const params = new URLSearchParams({
    businessUnitId: data.businessUnit || '',
    taskId: data.taskId,
  });
  return `${routePaths.TASK_DETAILS.path}?${params}`;
}

export const getTaskContent = (
  task: Task,
  selectedLanguage?: string,
  error?: (error: string) => void,
): taskContent => {
  error?.('');

  const defaultContent: taskContent = {
    taskName: task.taskName || task.localizedContent?.content?.taskName || '',
    description: task.description || task.localizedContent?.content?.description || '',
    auditorObservation:
      task.auditorObservation || task.localizedContent?.content?.auditorObservation || '',
    auditName: task.auditName || task.localizedContent?.content?.auditName || '',
    questionTitle:
      task.questionTitle || task.localizedContent?.content?.questionTitle || '',
  };

  if (!selectedLanguage) return defaultContent;

  const translatedContent =
    task.localizedContent?.translations?.[selectedLanguage] ||
    task.translationsMap?.[selectedLanguage];

  if (!translatedContent && error && selectedLanguage !== DEFAULT_LANGUAGE) {
    const languageLabel = LANGUAGE_LIST.find(
      (item) => item.value === selectedLanguage,
    )?.label;
    error(
      `The translation is currently unavailable for ${languageLabel} (${selectedLanguage}) language.`,
    );
  }

  return translatedContent || defaultContent;
};
